/**
 * ETFHoldingsMomentumMatrix — a single wide table where every ETF constituent
 * appears with its industry, market cap, weight, and the *full* returns
 * matrix (1D / 5D / 10D / 20D / 50D / 100D / 200D / 1Y / 3Y / 5Y) so the
 * user can eyeball basket-level momentum at a glance.
 *
 * All columns are sortable (click header → sort desc, click again → asc);
 * nulls always sink to the bottom regardless of direction.
 *
 * Layout mirrors the GICS Sector & Sub-Sector table + All Stocks table:
 *   • black surface, white body text
 *   • sky-blue ticker hyperlinks
 *   • green % gains, red % losses
 *   • chevron on the active sort column
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, RefreshCw, BarChart3 } from "lucide-react";
import { API } from "../config/api";

const RETURN_COLS = [
  { key: "ret_1d",   label: "1D"   },
  { key: "ret_5d",   label: "5D"   },
  { key: "ret_10d",  label: "10D"  },
  { key: "ret_20d",  label: "20D"  },
  { key: "ret_50d",  label: "50D"  },
  { key: "ret_100d", label: "100D" },
  { key: "ret_200d", label: "200D" },
  { key: "ret_1y",   label: "1Y"   },
  { key: "ret_3y",   label: "3Y"   },
  { key: "ret_5y",   label: "5Y"   },
];

const fmtPct = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`);
const pctClass = (v) =>
  v == null ? "text-gray-500"
  : v >= 0  ? "text-green-400"
            : "text-red-400";
const fmtPrice = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
const fmtMcap = (v) => {
  if (v == null) return "—";
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9)  return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6)  return `$${(v / 1e6).toFixed(2)}M`;
  return `$${v.toFixed(0)}`;
};
const fmtWeight = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}%`);

export default function ETFHoldingsMomentumMatrix({ ticker }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sort defaults to weight-descending so the largest holdings show first —
  // matches the natural expectation of "biggest constituents on top".
  const [sortKey, setSortKey] = useState("weight_pct");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/stock/etf-holdings-full-returns/${encodeURIComponent(ticker)}`, { timeout: 90000 })
      .then((r) => {
        if (cancelled) return;
        const holdings = r.data?.holdings || [];
        setRows(holdings);
      })
      .catch((e) => { if (!cancelled) setError(e?.message || "Failed to load."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticker]);

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const aNull = av == null;
      const bNull = bv == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof av === "string" && typeof bv === "string") {
        return sortDir === "desc" ? bv.localeCompare(av) : av.localeCompare(bv);
      }
      return sortDir === "desc" ? bv - av : av - bv;
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  const onHeader = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ active }) => {
    if (!active) return null;
    return sortDir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />;
  };

  if (!ticker || (rows.length === 0 && !loading && !error)) return null;

  return (
    <div
      className="rounded-lg border border-emerald-900/40 bg-black text-white p-3 space-y-2"
      data-testid="etf-holdings-momentum-matrix"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <BarChart3 className="w-4 h-4 text-emerald-400" />
        <span className="text-sm font-semibold">Momentum Matrix</span>
        <span className="font-mono text-cyan-300 font-bold">{ticker}</span>
        <span className="text-[10px] text-gray-400 font-normal">
          · {rows.length} holdings · click any header to sort high→low
        </span>
        {loading && <RefreshCw className="w-3 h-3 animate-spin text-emerald-400 ml-2" />}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[11px] min-w-[1400px]">
          <thead>
            <tr className="text-[9px] text-gray-500 uppercase tracking-wider border-b border-gray-800 bg-[#0d0d0d]">
              <th className="px-2 py-2 text-left cursor-pointer hover:text-white" onClick={() => onHeader("symbol")} data-testid="mm-header-symbol">
                <span className="inline-flex items-center gap-1">Symbol <SortIcon active={sortKey === "symbol"} /></span>
              </th>
              <th className="px-2 py-2 text-left cursor-pointer hover:text-white" onClick={() => onHeader("name")} data-testid="mm-header-name">
                <span className="inline-flex items-center gap-1">Company <SortIcon active={sortKey === "name"} /></span>
              </th>
              <th className="px-2 py-2 text-left cursor-pointer hover:text-white" onClick={() => onHeader("industry")} data-testid="mm-header-industry">
                <span className="inline-flex items-center gap-1">Industry <SortIcon active={sortKey === "industry"} /></span>
              </th>
              <th className="px-2 py-2 text-right cursor-pointer hover:text-white" onClick={() => onHeader("market_cap")} data-testid="mm-header-mcap">
                <span className="inline-flex items-center gap-1 justify-end w-full">Mkt Cap <SortIcon active={sortKey === "market_cap"} /></span>
              </th>
              <th className="px-2 py-2 text-right cursor-pointer hover:text-white" onClick={() => onHeader("price")} data-testid="mm-header-price">
                <span className="inline-flex items-center gap-1 justify-end w-full">Price <SortIcon active={sortKey === "price"} /></span>
              </th>
              <th className="px-2 py-2 text-right cursor-pointer hover:text-white" onClick={() => onHeader("weight_pct")} data-testid="mm-header-weight">
                <span className="inline-flex items-center gap-1 justify-end w-full">Weight <SortIcon active={sortKey === "weight_pct"} /></span>
              </th>
              {RETURN_COLS.map((c) => (
                <th
                  key={c.key}
                  className={`px-2 py-2 text-right cursor-pointer hover:text-white ${sortKey === c.key ? "text-emerald-300 font-bold" : ""}`}
                  onClick={() => onHeader(c.key)}
                  data-testid={`mm-header-${c.key}`}
                >
                  <span className="inline-flex items-center gap-1 justify-end w-full">
                    {c.label}
                    <SortIcon active={sortKey === c.key} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {error && (
              <tr>
                <td colSpan={16} className="px-3 py-4 text-center text-red-400 text-xs">{error}</td>
              </tr>
            )}
            {!error && loading && rows.length === 0 && (
              <tr>
                <td colSpan={16} className="px-3 py-8 text-center text-gray-400 text-xs">
                  <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-2 text-emerald-500" />
                  Computing 5-year returns across every holding…
                </td>
              </tr>
            )}
            {sorted.map((r) => (
              <tr
                key={r.symbol}
                className="border-b border-gray-900 hover:bg-gray-900/40 transition-colors"
                data-testid={`mm-row-${r.symbol}`}
              >
                <td className="px-2 py-1.5 font-mono font-bold">
                  <Link
                    to={`/stock?ticker=${encodeURIComponent(r.symbol)}`}
                    className="text-sky-300 hover:text-sky-200 hover:underline"
                    data-testid={`mm-row-${r.symbol}-symbol`}
                  >
                    {r.symbol}
                  </Link>
                </td>
                <td className="px-2 py-1.5 truncate max-w-[240px]" title={r.name || ""}>
                  {r.name || "—"}
                </td>
                <td className="px-2 py-1.5 truncate max-w-[180px]" title={r.industry || ""}>
                  {r.industry || "—"}
                </td>
                <td className="px-2 py-1.5 text-right font-mono">{fmtMcap(r.market_cap)}</td>
                <td className="px-2 py-1.5 text-right font-mono">{fmtPrice(r.price)}</td>
                <td className="px-2 py-1.5 text-right font-mono text-amber-300 font-bold">{fmtWeight(r.weight_pct)}</td>
                {RETURN_COLS.map((c) => (
                  <td
                    key={c.key}
                    className={`px-2 py-1.5 text-right font-mono ${pctClass(r[c.key])}`}
                    data-testid={`mm-row-${r.symbol}-${c.key}`}
                  >
                    {fmtPct(r[c.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-gray-500 leading-snug">
        Industry &amp; market cap sourced from the same universe scrape that powers the All Stocks page.
        All return windows count exact trading days (Yahoo daily bars are market-day-only, so 5D = 5 sessions ago, not 5 calendar days).
      </p>
    </div>
  );
}
